import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { useTheme } from "@mui/material";
import { HIDE_DECIMAL, useConfig } from "@/hooks/useConfig";
import { Divider } from "@/Components/v2/atoms/Divider";
import React from "react";
import { ActionPlanStatusMapping } from "../../action-plans/types";
import { getNumberPrefix } from "@/shared/utils/getNumberPrefix";
import { ActionPlanStatus } from "@mutiny-group/sbo-openapi-client";

type RevenueMetricCardsProps = {
  status: ActionPlanStatus;
  liveRevenueForecast: number;
  revenueTarget: number;
  currentRevenueDifference: number;
};

const RevenueMetricCards = ({
  status,
  liveRevenueForecast,
  revenueTarget,
  currentRevenueDifference,
}: RevenueMetricCardsProps) => {
  const theme = useTheme();
  const { config } = useConfig();
  const { formatCurrency } = config.currency;
  const { style } = ActionPlanStatusMapping[status];

  const formattedCurrentRevenueDifference = `${getNumberPrefix(currentRevenueDifference)}${formatCurrency(currentRevenueDifference, HIDE_DECIMAL)}`;

  const cards = [
    {
      label: "Goal Revenue",
      value: formatCurrency(revenueTarget, HIDE_DECIMAL),
      description: "Your original goal revenue when the action plan was created",
    },
    {
      label: "Current Forecast Revenue",
      value: formatCurrency(liveRevenueForecast, HIDE_DECIMAL),
      description: "The revenue you are predicted to generate based on your last refresh",
    },
    {
      label: "Difference",
      value: formattedCurrentRevenueDifference,
      description: "The difference between your original goal and current forecast",
      valueColor: style.color,
    },
  ];

  return (
    <Stack
      direction="column"
      sx={{
        minWidth: 240,
        maxWidth: 280,
      }}
      borderRight={`1px solid ${theme.palette.divider}`}
    >
      {cards.map((card, index) => (
        <React.Fragment key={index}>
          <Stack key={index} margin={3} flexGrow={1} justifyContent="center">
            <Typography variant="body2" fontSize={14} fontWeight={500}>
              {card.label}
            </Typography>
            <Typography fontSize={24} fontWeight={600} sx={{ color: card.valueColor }}>
              {card.value}
            </Typography>
            <Typography fontSize={12}>{card.description}</Typography>
          </Stack>
          {index !== cards.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default RevenueMetricCards;
